/** The tracks a member is on, as a column of the project's Members
 *  table, with a dialog to change them. The platform asks for this
 *  through ``memberColumns``; it shows only where the project is split
 *  into tracks. */
import { useState } from "react";
import { apiUrl } from "@afriannotate/app/utils/jwt-storage";

const WORD = { members: "", reviewers: "reviews" };

/** "Maize, Millet, Sorghum", or nothing when a track answers only to
 *  its own name. */
const covers = (track) => {
  const values = (track.values ?? []).filter(Boolean);
  if (!values.length || (values.length === 1 && values[0] === track.name)) return "";
  return values.join(", ");
};

const named = (track) => {
  const what = covers(track);
  return what ? `${track.name} (${what})` : track.name;
};
// What one person does on one track.
const DOES = [
  { value: "", label: "Not on it" },
  { value: "annotates", label: "Annotates" },
  { value: "reviews", label: "Reviews" },
  { value: "both", label: "Annotates and reviews" },
];

const read = async (projectId) => {
  const res = await fetch(apiUrl(`/api/ext/tracks/projects/${projectId}/`), { credentials: "include" });
  return res.ok ? res.json() : null;
};

/** ``{user id: [{name, role}]}`` from the project's tracks. */
const load = async (projectId) => {
  const body = await read(projectId);
  const out = {};
  for (const track of body?.tracks ?? []) {
    for (const role of ["members", "reviewers"]) {
      for (const id of track[role] ?? []) {
        out[id] = out[id] ?? [];
        if (!out[id].some((t) => t.name === track.name && t.role === role)) {
          out[id].push({ name: track.name, role, covers: covers(track) });
        }
      }
    }
  }
  return out;
};

/** The track with this person put where the choice says. */
const asked = (track, userId, does) => {
  const members = (track.members ?? []).filter((id) => id !== userId);
  const reviewers = (track.reviewers ?? []).filter((id) => id !== userId);
  if (does === "annotates" || does === "both") members.push(userId);
  if (does === "reviews" || does === "both") reviewers.push(userId);
  return { ...track, members, reviewers };
};

const doesOn = (track, userId) => {
  const annotates = (track.members ?? []).includes(userId);
  const reviews = (track.reviewers ?? []).includes(userId);
  if (annotates && reviews) return "both";
  if (reviews) return "reviews";
  if (annotates) return "annotates";
  return "";
};

/** A member row names its person by id; which key holds it depends on
 *  the page, so every shape the platform sends is read. */
const whoIs = (member) => {
  const user = member?.user;
  const id = typeof user === "number" ? user : (user?.id ?? member?.user_id ?? null);
  const name =
    [member?.user_first_name ?? user?.first_name, member?.user_last_name ?? user?.last_name]
      .filter(Boolean)
      .join(" ") ||
    member?.user_email ||
    user?.email ||
    "This person";
  return { id, name };
};

/** The same rows as a section of the Members page's edit dialog: it
 *  hands back a function the dialog calls when it saves. */
const TracksSection = ({ projectId, member, register }) => {
  const { id } = whoIs(member);
  const [body, setBody] = useState(null);
  const [picked, setPicked] = useState({});

  if (body === null) {
    read(projectId).then((found) => {
      setBody(found ?? false);
      setPicked(Object.fromEntries((found?.tracks ?? []).map((t) => [t.id, doesOn(t, id)])));
    });
  }

  register(async () => {
    if (!body) return;
    const tracks = (body.tracks ?? []).map((t) => asked(t, id, picked[t.id] ?? ""));
    const res = await fetch(apiUrl(`/api/ext/tracks/projects/${projectId}/`), {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode: body.mode, field: body.field, per_track: body.per_track, tracks }),
    });
    if (!res.ok) throw new Error("The tracks could not be saved.");
  });

  if (body === false) return <p className="lsf-dataset__note">The tracks of this project could not be read.</p>;
  if (!body) return <p className="lsf-dataset__note">Loading…</p>;
  return (
    <div className="lsf-tracks-settings__assign-rows">
      {(body.tracks ?? []).map((t) => (
        <label key={t.id} className="lsf-tracks-settings__assign-row">
          <span>
            {t.name}
            {covers(t) ? <em className="lsf-tracks-settings__hint"> {covers(t)}</em> : null}
          </span>
          <select
            className="lsf-tracks-settings__input"
            value={picked[t.id] ?? ""}
            onChange={(e) => setPicked({ ...picked, [t.id]: e.target.value })}
          >
            {DOES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      ))}
    </div>
  );
};

/** Putting the people being added onto tracks, from the Add dialog:
 *  a line per track, so several can be chosen at once. */
const TracksAddSection = ({ projectId, register }) => {
  const [body, setBody] = useState(null);
  const [picked, setPicked] = useState({});

  if (body === null) {
    read(projectId).then((found) => setBody(found ?? false));
  }

  register(async (userIds) => {
    const wanted = Object.entries(picked).filter(([, does]) => does);
    if (!body || !wanted.length || !userIds?.length) return;
    const tracks = (body.tracks ?? []).map((t) => {
      const does = picked[t.id] ?? "";
      return does ? userIds.reduce((acc, id) => asked(acc, id, does), t) : t;
    });
    const res = await fetch(apiUrl(`/api/ext/tracks/projects/${projectId}/`), {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode: body.mode, field: body.field, per_track: body.per_track, tracks }),
    });
    if (!res.ok) throw new Error("They were added, but the tracks could not be saved.");
  });

  if (!body || !(body.tracks ?? []).length) return null;
  return (
    <div className="lsf-tracks-settings__assign-rows">
      {(body.tracks ?? []).map((t) => (
        <label key={t.id} className="lsf-tracks-settings__assign-row">
          <span>
            {t.name}
            {covers(t) ? <em className="lsf-tracks-settings__hint"> {covers(t)}</em> : null}
          </span>
          <select
            className="lsf-tracks-settings__input"
            value={picked[t.id] ?? ""}
            onChange={(e) => setPicked({ ...picked, [t.id]: e.target.value })}
          >
            {DOES.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
      ))}
    </div>
  );
};

export const trackMemberColumn = {
  key: "tracks",
  label: "Tracks",
  edit: { label: "Tracks", Component: TracksSection },
  add: { label: "Put them on tracks", Component: TracksAddSection },
  shows: (project) => Boolean(project?.extensions?.tracks?.count),
  load,
  render: (member, loaded) => {
    const mine = loaded?.[whoIs(member).id] ?? [];
    return (
      <span className="lsf-tracks-settings__member-cell">
        {mine.length ? (
          <span className="lsf-tracks-settings__people">
            {mine.map((t) => (
              <span
                key={`${t.name}-${t.role}`}
                className="lsf-tracks-settings__pill"
                title={t.covers ? `Answers to ${t.covers}` : undefined}
              >
                <span className="lsf-tracks-settings__pill-name">{t.name}</span>
                {WORD[t.role] ? <em>{WORD[t.role]}</em> : null}
              </span>
            ))}
          </span>
        ) : (
          <span className="lsf-dataset__note">Not on a track</span>
        )}
      </span>
    );
  },
};
